import {toast} from 'react-toastify';
import {call, ForkEffect, put, select, takeLatest} from 'redux-saga/effects';
import {ActionCreators} from 'redux-undo';
import i18n from '../../i18n';
import {ISlidePreview} from '../../incase-domain/proptypes/slidePreview';
import {ISlide, SlideWithoutElements} from '../../incase-domain/slide';
import {deleteSlide, updateSlideWithoutElements} from '../../service/slides';
import {areSlidesDifferent} from '../../utils/slide';
import {appendSlidePreviewCacheBust} from '../../utils/slidePreviewUrl';
import {swapArrayElement, swapArrayIndices} from '../../utils/utils';
import {
    updateSelectedPresentationAction,
    updateSelectedPresentationSlideIndexListAction,
    updateSelectedPresentationSlidesAction,
} from '../actions/presentation';
import {
    ADD_EMITTED_SLIDE_ACTION,
    BRING_ELEMENT_TO_FRONT,
    COPY_EMITTED_SLIDE_ACTION,
    DELETE_SLIDE,
    IMPORT_EMITTED_SLIDE_ACTION,
    REMOVE_EMITTED_SLIDE_ACTION,
    SEND_ELEMENT_TO_BACK,
    setEmittedSlideAction,
    setHighlightedElementIndex,
    SWAP_EMITTED_SLIDE_ACTION,
    UPDATE_EMITTED_SLIDE_ACTION,
    UPDATE_EMITTED_SLIDE_PREVIEW_ACTION,
    UPDATE_SLIDE_WITHOUT_ELEMENTS_ACTION,
    updateSelectedSlideAction,
} from '../actions/slide';
import {updateSelectedSlidePreviewsAction} from '../actions/slide.preview';
import {startPresUpdateSpinnerAction, stopPresUpdateSpinnerAction} from '../actions/spinner';
import {
    IAddEmittedSlideAction,
    IBringElementToFrontAction,
    ICopyEmittedSlideAction,
    IDeleteSlideAction,
    IImportEmittedSlideAction,
    IRemoveEmittedSlideAction,
    ISendElementToBackAction,
    ISwapEmittedSlideAction,
    IUpdateEmittedSlideAction,
    IUpdateEmittedSlidePreviewAction,
    IUpdateSlideWithoutElementsAction,
} from '../actions/types/slide.types';
import {AppState} from '../reducer';

export function* updateSlideWithoutElementsSaga(action: IUpdateSlideWithoutElementsAction): unknown {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const slideWithoutElements: SlideWithoutElements = (({elements, ...rest}) => rest)(action.slide);
    const state: AppState = yield select();

    yield put(startPresUpdateSpinnerAction(true));
    const response: ISlide = yield call(updateSlideWithoutElements, slideWithoutElements);

    const updatedSlide = {...action.slide, ...response};

    const {selectedPresentation} = state.presentationReducer;
    const selectedPresentationSlides = selectedPresentation.slides;
    const updatesSlideIndex = selectedPresentationSlides.findIndex((slide) => slide._id === updatedSlide._id);
    const updatedSlideList = swapArrayElement(selectedPresentationSlides, updatesSlideIndex, updatedSlide);

    yield put(updateSelectedPresentationSlidesAction(updatedSlideList));
    yield put(updateSelectedSlideAction(updatedSlide, updatesSlideIndex, false));
    yield put(stopPresUpdateSpinnerAction());
}

export function* deleteSlideSaga(action: IDeleteSlideAction): unknown {
    const {slide} = action;
    yield put(startPresUpdateSpinnerAction(true));
    const slideDeleteResponse: {removed: boolean; removedId: string} = yield call(deleteSlide, slide);
    if (!!slideDeleteResponse && slideDeleteResponse.removed) {
        yield call(toast.success, i18n.t('toasts.deleteSlideSuccess'));

        const state: AppState = yield select();
        const {selectedSlide} = state.slideReducer.present;
        const {selectedPresentation} = state.presentationReducer;
        const selectedPresentationSlides = selectedPresentation.slides;
        const selectedPresentationSlideIndexList = selectedPresentation.slideIndexList;

        const indexOfDeletedSlide = selectedPresentationSlides.findIndex((s) => s._id === slide._id);
        if (selectedSlide._id === slide._id) {
            const newSelectedSlideIndex = indexOfDeletedSlide === 0 ? 1 : indexOfDeletedSlide - 1;
            yield put(updateSelectedSlideAction(selectedPresentationSlides[newSelectedSlideIndex], newSelectedSlideIndex, false));
        }

        const updatedSlideList = selectedPresentationSlides.filter((selectedSlide) => {
            return selectedSlide._id !== slide._id;
        });

        const updatedSlideIndexList = selectedPresentationSlideIndexList.filter((selectedSlideIndex) => {
            return selectedSlideIndex !== slide._id;
        });

        yield put(updateSelectedPresentationSlideIndexListAction(updatedSlideIndexList));
        yield put(updateSelectedPresentationSlidesAction(updatedSlideList));
        yield put(stopPresUpdateSpinnerAction());
        yield put(ActionCreators.clearHistory());
    } else {
        yield put(stopPresUpdateSpinnerAction());
        yield call(toast.error, i18n.t('toasts.deleteSlideError'));
    }
}

export function* addEmittedSlideSaga(action: IAddEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    if (!action.slides || !action.slideIndexList) return;

    const isAlreadyUpdated =
        new Set([...selectedPresentation.slideIndexList, ...action.slideIndexList]).size === selectedPresentation.slideIndexList.length;
    if (isAlreadyUpdated) return;

    yield put(startPresUpdateSpinnerAction(true));

    const actionSlide = action.slides[0];
    const index = action.slideIndexList.indexOf(actionSlide._id || '');
    const newSelectedPresentationSlides = [...selectedPresentation.slides];
    newSelectedPresentationSlides.splice(index, 0, actionSlide);

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(action.slideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* removeEmittedSlideSaga(action: IRemoveEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    const {selectedSlide} = state.slideReducer.present;

    yield put(startPresUpdateSpinnerAction(true));

    const newSelectedPresentationSlides = selectedPresentation.slides.filter((selectedSlide) => selectedSlide._id !== action.slideId);
    const newSelectedPresentationSlideIndexList = selectedPresentation.slideIndexList.filter(
        (selectedSlideIndex) => selectedSlideIndex !== action.slideId
    );

    if (selectedSlide._id === action.slideId) {
        const index = selectedPresentation.slideIndexList.indexOf(action.slideId || '');
        if (index === 0) {
            const selectedSlide = selectedPresentation.slides[index + 1];
            yield put(updateSelectedSlideAction(selectedSlide, index + 1, false));
        } else {
            const selectedSlide = selectedPresentation.slides[index - 1];
            yield put(updateSelectedSlideAction(selectedSlide, index - 1, false));
        }
    }

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(newSelectedPresentationSlideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* copyEmittedSlideSaga(action: ICopyEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;
    yield put(startPresUpdateSpinnerAction(true));

    const copiedSlideIndex = selectedPresentation.slideIndexList.findIndex((id) => id === action.slideId);
    const newSlideIndexList = [...selectedPresentation.slideIndexList];
    newSlideIndexList.splice(copiedSlideIndex + 1, 0, action.slide?._id);

    const newSelectedPresentationSlides = [...selectedPresentation.slides];
    newSelectedPresentationSlides.splice(copiedSlideIndex + 1, 0, action.slide);

    yield put(updateSelectedPresentationSlidesAction(newSelectedPresentationSlides));
    yield put(updateSelectedPresentationSlideIndexListAction(newSlideIndexList));
    yield put(stopPresUpdateSpinnerAction());
}

export function* updateEmittedSlideSaga(action: IUpdateEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;
    const {selectedSlide} = state.slideReducer.present;

    yield put(startPresUpdateSpinnerAction(true));

    if (selectedSlide._id === action.slide._id && areSlidesDifferent(selectedSlide, action.slide)) {
        yield put(setEmittedSlideAction(action.slide));
    } else if (selectedPresentation.slideIndexList.length > 1) {
        const updatesSlideIndex = selectedPresentation.slides.findIndex((slide) => slide._id === action.slide?._id);
        const updatedSlideList = swapArrayElement(selectedPresentation.slides, updatesSlideIndex, action.slide);

        yield put(updateSelectedPresentationSlidesAction(updatedSlideList));
        yield put(updateSelectedSlidePreviewsAction(selectedPresentationSlidePreviews));
    }
    yield put(stopPresUpdateSpinnerAction());
}

export function* updateEmittedSlidePreviewSaga(action: IUpdateEmittedSlidePreviewAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentationSlidePreviews} = state.presentationReducer;
    const slidePreview: ISlidePreview = {
        ...action.slidePreview,
        imgSrc: appendSlidePreviewCacheBust(action.slidePreview.imgSrc, Date.now()),
    };
    const updatesSlidePreviewIndex = selectedPresentationSlidePreviews.findIndex((preview) => preview.slideId === slidePreview.slideId);
    let updatedSlidePreviewList: ISlidePreview[];
    if (updatesSlidePreviewIndex >= 0) {
        updatedSlidePreviewList = swapArrayElement(selectedPresentationSlidePreviews, updatesSlidePreviewIndex, slidePreview);
    } else {
        updatedSlidePreviewList = [...selectedPresentationSlidePreviews, slidePreview];
    }

    yield put(updateSelectedSlidePreviewsAction(updatedSlidePreviewList));
}

export function* importEmittedSlideSaga(action: IImportEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation} = state.presentationReducer;

    yield put(startPresUpdateSpinnerAction(true));

    yield put(
        updateSelectedPresentationAction({
            ...action.presentation,
            slides: [...selectedPresentation.slides, ...action.slides],
        })
    );

    yield put(stopPresUpdateSpinnerAction());
}

export function* swapEmittedSlideSaga(action: ISwapEmittedSlideAction): unknown {
    const state: AppState = yield select();
    const {selectedPresentation, selectedPresentationSlidePreviews} = state.presentationReducer;
    yield put(startPresUpdateSpinnerAction(true));

    const comparator = (a: ISlide, b: ISlide): number => {
        const idA = action.slideIndexList.indexOf(a._id);
        const idB = action.slideIndexList.indexOf(b._id);
        return idA - idB;
    };

    const sortedSlides = selectedPresentation.slides.sort(comparator);

    const updatedPresentation = {
        ...selectedPresentation,
        slideIndexList: action.slideIndexList,
        slides: sortedSlides,
    };
    yield put(updateSelectedPresentationAction(updatedPresentation));
    yield put(updateSelectedSlidePreviewsAction(selectedPresentationSlidePreviews));
    yield put(stopPresUpdateSpinnerAction());
}

export function* bringElementToFrontSaga(action: IBringElementToFrontAction): unknown {
    const state: AppState = yield select();
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    const newIndex = selectedSlide.elements.length - 1 || 0;
    const newElements = swapArrayIndices(selectedSlide.elements, newIndex, action.elementIndex);

    yield put(updateSelectedSlideAction({...selectedSlide, elements: newElements}, selectedSlideIndex, true));
    yield put(setHighlightedElementIndex(newIndex));
}

export function* sendElementToBackSaga(action: ISendElementToBackAction): unknown {
    const state: AppState = yield select();
    const {selectedSlide, selectedSlideIndex} = state.slideReducer.present;

    const newElements = swapArrayIndices(selectedSlide.elements, 0, action.elementIndex);

    yield put(updateSelectedSlideAction({...selectedSlide, elements: newElements}, selectedSlideIndex, true));
    yield put(setHighlightedElementIndex(0));
}

export function* watchSlideSaga(): Generator<ForkEffect<never>, void> {
    yield takeLatest(DELETE_SLIDE, deleteSlideSaga);
    yield takeLatest(UPDATE_SLIDE_WITHOUT_ELEMENTS_ACTION, updateSlideWithoutElementsSaga);
    yield takeLatest(ADD_EMITTED_SLIDE_ACTION, addEmittedSlideSaga);
    yield takeLatest(REMOVE_EMITTED_SLIDE_ACTION, removeEmittedSlideSaga);
    yield takeLatest(COPY_EMITTED_SLIDE_ACTION, copyEmittedSlideSaga);
    yield takeLatest(UPDATE_EMITTED_SLIDE_ACTION, updateEmittedSlideSaga);
    yield takeLatest(UPDATE_EMITTED_SLIDE_PREVIEW_ACTION, updateEmittedSlidePreviewSaga);
    yield takeLatest(IMPORT_EMITTED_SLIDE_ACTION, importEmittedSlideSaga);
    yield takeLatest(SWAP_EMITTED_SLIDE_ACTION, swapEmittedSlideSaga);
    yield takeLatest(BRING_ELEMENT_TO_FRONT, bringElementToFrontSaga);
    yield takeLatest(SEND_ELEMENT_TO_BACK, sendElementToBackSaga);
}
